import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, PlayCircle, Sparkles, CalendarDays, Video } from "lucide-react";
import { STATS, SHOWREEL_URL, SHOWREEL_POSTER } from "../../data";
import { useSiteContent } from "../../lib/content";
import { MaskLine, Counter, Magnetic, scrollToId } from "./primitives";
import { Button } from "../ui/button";

export default function Hero() {
  const { content } = useSiteContent();
  const hero = content.hero || {};
  const stats = content.stats?.length ? content.stats : STATS;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // 3D mouse tilt on the hero spotlight image
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 200, damping: 22 });
  const sry = useSpring(ry, { stiffness: 200, damping: 22 });
  const onTilt = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const nx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
    const ny = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
    ry.set(nx * 8);
    rx.set(-ny * 8);
  };
  const onTiltLeave = () => { rx.set(0); ry.set(0); };

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden grain pt-28 pb-16">
      {/* Animated orbs */}
      <motion.div
        className="orb bg-[#FF1F8E] h-[38rem] w-[38rem] -top-40 -left-40"
        animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="orb bg-[#F97316] h-[32rem] w-[32rem] top-20 right-0"
        animate={{ x: [0, -50, 0], y: [0, 60, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="absolute inset-0 noise-bg opacity-40" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 w-full grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-border/15 bg-background/50 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-8"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-gradient" />
            {hero.kicker || "360° Business Growth Partner"}
          </motion.div>

          <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            <MaskLine delay={0.15}>{hero.line1 || "Transforming"}</MaskLine>
            <MaskLine delay={0.28}>{hero.line2 || "businesses through"}</MaskLine>
            <MaskLine delay={0.41}><span className="text-gradient">{hero.line3_gradient || "strategy, creativity"}</span></MaskLine>
            <MaskLine delay={0.54}>{hero.line4 || "\u0026 innovation."}</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-8 max-w-xl text-lg text-muted-foreground leading-relaxed"
          >
            {hero.sub || "We help ambitious brands grow with integrated Events, Branding, Digital Marketing, Media Production and Technology — under one roof."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Magnetic>
              <Button data-testid="hero-cta-primary" onClick={() => scrollToId("#contact")} className="group rounded-full h-13 px-7 py-6 text-base bg-brand-gradient text-white border-0 hover:opacity-90 shimmer-card">
                {hero.primary_cta || "Get Free Consultation"}
                <ArrowUpRight className="ml-1 h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
            </Magnetic>
            <Magnetic strength={0.2}>
              <Button data-testid="hero-cta-secondary" onClick={() => scrollToId("#portfolio")} variant="outline" className="rounded-full h-13 px-7 py-6 text-base border-border/20 bg-transparent hover:border-primary hover:text-primary">
                <PlayCircle className="mr-1 h-5 w-5" /> {hero.secondary_cta || "View Portfolio"}
              </Button>
            </Magnetic>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
          >
            {stats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl md:text-4xl font-bold text-gradient">
                  <Counter to={Number(s.value) || 0} suffix={s.suffix || ""} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Hero image spotlight with 3D tilt + floating chips */}
        <motion.div style={{ y: yImg }} className="lg:col-span-5 relative" onMouseMove={onTilt} onMouseLeave={onTiltLeave}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: -3 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: srx, rotateY: sry, transformPerspective: 900 }}
            className="relative rounded-[2rem] overflow-hidden border border-border/15 shadow-2xl shadow-primary/10 will-change-transform"
          >
            <video
              src={SHOWREEL_URL}
              poster={SHOWREEL_POSTER}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              data-testid="hero-showreel-video"
              className="w-full h-[30rem] object-cover bg-black"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90 border border-white/15">
              <span className="h-2 w-2 rounded-full bg-brand-gradient animate-pulse" /> Brand Reel
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
              className="absolute bottom-5 left-5 right-5 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20 p-4 flex items-center justify-between"
            >
              <div>
                <div className="text-white text-sm font-semibold">Live campaign</div>
                <div className="text-white/70 text-xs">+218% pipeline in 90 days</div>
              </div>
              <div className="h-10 w-10 grid place-items-center rounded-full bg-brand-gradient text-white">
                <ArrowUpRight className="h-5 w-5" />
              </div>
            </motion.div>
          </motion.div>

          {/* Floating chips (decorative) */}
          <FloatingChip className="hidden md:flex -top-6 -left-8" delay={1.3} icon={CalendarDays} label="Live Events" caption="1,200 delegates" />
          <FloatingChip className="hidden md:flex -bottom-6 -right-8" delay={1.5} icon={Video} label="Brand Films" caption="2.4M views / 30d" />
          <FloatingChip className="hidden lg:flex top-1/3 -right-16" delay={1.7} icon={Sparkles} label="AI Growth" caption="+3.1x pipeline" />
        </motion.div>
      </div>
    </section>
  );
}

function FloatingChip({ className = "", delay = 1.2, icon: Icon, label, caption }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.9 }}
      animate={{ opacity: 1, y: [0, -8, 0], scale: 1 }}
      transition={{ opacity: { delay, duration: 0.6 }, scale: { delay, duration: 0.6 }, y: { delay: delay + 0.2, duration: 4, repeat: Infinity, ease: "easeInOut" } }}
      className={`absolute z-20 items-center gap-3 rounded-2xl backdrop-blur-xl bg-background/70 border border-border/15 shadow-xl px-3 py-2.5 ${className}`}
    >
      <span className="h-8 w-8 grid place-items-center rounded-xl bg-brand-gradient text-white shrink-0"><Icon className="h-4 w-4" /></span>
      <div className="pr-1">
        <div className="text-[13px] font-semibold leading-tight">{label}</div>
        <div className="text-[11px] text-muted-foreground leading-tight">{caption}</div>
      </div>
    </motion.div>
  );
}

